import React, { useEffect, useReducer } from "react";
import { isFunction, get } from 'lodash';
import Spinner from 'components/Spinner';
const createPromise = (handler, props) => {
  if (!props[handler]) {
    console.warn(`Property [${handler}] not found`);
    return false;
  }
  return props[handler]();
};

type AsyncConnectState = {
  showSpinner: boolean;
  loaded: boolean;
  hasError: boolean;
  error?: Error;
}

enum AsyncConnectActions {
  startLoading = 'startLoading',
  finishLoading = 'finishLoading',
  setError = 'setError'
}

type StartLoadingAction = {
  type: AsyncConnectActions.startLoading
}

type FinishLoadingAction = {
  type: AsyncConnectActions.finishLoading
}

type SetErrorAction = {
  type: AsyncConnectActions.setError,
  payload: Error
}

const asyncConnectReducer = (
  state: AsyncConnectState,
  action: StartLoadingAction | FinishLoadingAction | SetErrorAction
): AsyncConnectState => {
  switch (action.type) {
    case AsyncConnectActions.startLoading:
      return { ...state, showSpinner: true, loaded: false }
    case AsyncConnectActions.finishLoading:
      return { ...state, showSpinner: false, loaded: true }
    case AsyncConnectActions.setError:
      return { ...state, error: action.payload, loaded: true, hasError: true, showSpinner: false }
    default:
      return state
  }  
};

const startLoading = (): StartLoadingAction => ({ type: AsyncConnectActions.startLoading });

const finishLoading = (): FinishLoadingAction => ({ type: AsyncConnectActions.finishLoading });

const setError = (error: Error): SetErrorAction => ({ type: AsyncConnectActions.setError, payload: error });

export const asyncConnect = (...handlers) => BaseComponent => {
  const factory = React.createFactory(BaseComponent);

  return (props) => {
    const [state, dispatch] = useReducer(asyncConnectReducer, {
      showSpinner: false,
      loaded: false,
      hasError: false
    })

    const lastHandler = handlers[handlers.length - 1];
    const dependencies = Array.isArray(lastHandler)
      ? lastHandler.map(path => get(props, path))
      : [];
    const handlersToExecute = dependencies.length > 0
      ? handlers.slice(0, handlers.length - 1)
      : handlers;

    useEffect(() => {
      dispatch(startLoading());

      Promise.all(
        handlersToExecute.map((handler) => isFunction(handler)
          ? handler(props)
          : createPromise(handler, props)
        )
      )
      .then(() => dispatch(finishLoading()))
      .catch(e => dispatch(setError(e)));
    }, dependencies);

    return state.loaded
      ? factory({ ...state, ...props })
      : state.showSpinner
        && props.placeholder ? props.placeholder : React.createElement(Spinner);
  };
}
